<template>
  <div v-if="pageLoaded">
    <!-- Se for blog, renderiza o blog, senão renderiza o resto -->
    <RenderBlog v-if="page.type === 'blog'" :page="page" :lang="currentLang" />
    <template v-else>
      <!-- Navbar -->
      <component :is="navbarComponent"/>

      <!-- Hero Section -->
      <div
          :style="{ backgroundColor: page.color || '#111' }"
          class="relative bg-gray-900 pt-20 md:pt-24 lg:pt-24 overflow-hidden">
        <div
            class="flex flex-col items-center justify-center relative w-full h-[950px] transition-opacity duration-700"
            :class="pageLoaded ? 'animate-fade-right delay-100' : ''">

          <div class="relative w-full h-full overflow-hidden">
            <template v-if="pageLoaded">
              <img :src="page.image" alt="Descrição da imagem"  class="w-full h-full object-cover"  >
              <div class="absolute inset-x-0 bottom-0 h-[50%] bg-gradient-to-t"
                   :style="{ background: `linear-gradient(to top, ${page.color}, transparent)` }"></div>
            </template>
            <template v-else>
              <div class="w-full h-full bg-gray-700 animate-pulse"></div>
            </template>
          </div>

          <div class="flex flex-wrap space-x-3 py-5">
            <template v-if="pageLoaded">
              <div class="flex flex-col sm:flex-row sm:flex-wrap sm:space-x-3 space-y-3 sm:space-y-0 py-5 items-center">
                <div v-for="(button, index) in page.buttons" :key="index" class="">
                  <a :href="button.link" target="_blank" class="flex items-center justify-center text-white rounded-md">
                    <img v-if="button.image" :src="button.image" alt="Button Image" class="h-14 mr-2" />
                    {{ button.text }}
                  </a>
                </div>
              </div>
            </template>
            <template v-else>
              <div v-for="index in 3" :key="index" class="h-14 w-48 bg-gray-700 animate-pulse rounded-md m-2"></div>
            </template>
          </div>
        </div>
      </div>

      <!-- YOUTUBE Section -->
      <div :style="{ backgroundColor: page.color || '#111' }"
           class="flex justify-center items-center py-5 transition-opacity duration-700"
           :class="pageLoaded ? 'animate-fade-right delay-300' : ''">
        <div class="py-2 mx-5 rounded-md max-w-6xl justify-center text-white lg:flex md:items-center">
          <template v-if="pageLoaded">
            <div>
              <p class="mt-2 text-3xl lg:text-4xl font-bold text-white font-roboto text-center">
                <span v-if="isHtmlString(page.title1)" v-html="page.title1"></span>
                <span v-else>{{ page.title1 }}</span>
              </p>
              <p class="mt-2 font-light py-8 text-lg font-roboto text-center">
                <span v-if="isHtmlString(page.description1)" v-html="page.description1"></span>
                <span v-else>{{ page.description1 }}</span>
              </p>
              <div class="md:min-w-[700px] md:h-[500px] h-80 flex items-center justify-center shadow-none rounded-3xl overflow-hidden border-4 border-gray-700">
                <iframe :src="youtubeEmbedLink" frameborder="0" allowfullscreen class="w-full h-full"></iframe>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col items-center w-full">
              <div class="h-10 w-80 bg-gray-700 animate-pulse rounded mb-6"></div>
              <div class="h-6 w-96 bg-gray-700 animate-pulse rounded mb-6"></div>
              <div class="md:min-w-[700px] md:h-[500px] h-80 bg-gray-700 animate-pulse rounded-3xl border-4 border-gray-700"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- CAROUSEL Section -->
      <div
          v-if="pageLoaded && (page.title2 || page.description2 || (page.images && page.images.length > 0))"
          :style="{ backgroundColor: page.color || '#111' }"
          class="bg-white justify-center flex flex-col items-center py-10 text-[#00a0f1] transition-opacity duration-700"
          :class="pageLoaded ? 'animate-fade-right delay-500' : ''"
      >
        <div class="mx-10 max-w-6xl">
          <h1
              v-if="page.title2"
              class="mt-2 font-black py-3 text-white text-3xl font-roboto md:text-4xl lg:text-5xl text-center"
              :style="{ color: page.colorSecondary }"
          >
            <span v-if="isHtmlString(page.title2)" v-html="page.title2"></span>
            <span v-else>{{ page.title2 }}</span>
          </h1>
          <p
              v-if="page.description2"
              class="mt-3 md:mx-36 py-3 text-center text-white items-center font-roboto md:text-lg text-md"
          >
            <span v-if="isHtmlString(page.description2)" v-html="page.description2"></span>
            <span v-else>{{ page.description2 }}</span>
          </p>
        </div>
        <div class="flex justify-center items-center" v-if="page.images && page.images.length > 0">
          <div class="mx-5 lg:mx-0 flex flex-col gap-12">
            <CarrosselMobile
                v-if="page.platform === 'mobile'"
                :images="page.images"
                :pageColor="page.color"
            />
            <CarrosselDesktop
                v-else
                :images="page.images"
            />
          </div>
        </div>
      </div>
      <div
          v-else-if="!pageLoaded"
          :style="{ backgroundColor: page.color || '#111' }"
          class="bg-white justify-center flex flex-col items-center py-10 text-[#00a0f1] transition-opacity duration-700"
      >
        <div class="h-10 w-80 bg-gray-700 animate-pulse rounded mb-6 mx-auto"></div>
        <div class="h-6 w-96 bg-gray-700 animate-pulse rounded mb-6 mx-auto"></div>
        <div class="h-64 w-96 bg-gray-700 animate-pulse rounded-2xl"></div>
      </div>

      <!-- CTA Section -->
      <div :style="{ backgroundColor: page.color || '#111' }"
           class="flex justify-center items-center py-5 transition-opacity duration-700"
           :class="pageLoaded ? 'animate-fade-right delay-700' : ''">
        <div class="py-2 mx-5 rounded-md max-w-6xl justify-center text-white lg:flex md:items-center">
          <template v-if="pageLoaded">
            <div>
              <p class="mt-2 font-black md:mt-6 py-3 text-3xl text-white font-roboto md:text-4xl lg:text-5xl text-center md:text-start">
                <span v-if="isHtmlString(page.title3)" v-html="page.title3"></span>
                <span v-else>{{ page.title3 }}</span>
              </p>
              <p class="mt-3 max-w-lg text-center py-3 font-roboto md:text-lg text-md md:text-start">
                <span v-if="isHtmlString(page.description3)" v-html="page.description3"></span>
                <span v-else>{{ page.description3 }}</span>
              </p>
              <div
                  class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 place-items-center md:place-items-start"
              >
                <div v-for="(button, index) in page.buttons" :key="index" class="">
                  <a :href="button.link" target="_blank" class="flex items-center justify-center text-white rounded-md">
                    <img v-if="button.image" :src="button.image" alt="Button Image" class="h-12 mr-2" />
                    {{ button.text }}
                  </a>
                </div>
              </div>
            </div>
            <div class="flex items-center justify-center shadow-none overflow-hidden">
              <img class="rounded-3xl shadow-lg" :src="page.imageCta" alt="CTA Imagem" />
            </div>
          </template>
          <template v-else>
            <div class="flex flex-col gap-6 items-center w-full">
              <div class="h-10 w-80 bg-gray-700 animate-pulse rounded"></div>
              <div class="h-6 w-96 bg-gray-700 animate-pulse rounded"></div>
              <div class="h-64 w-64 bg-gray-700 animate-pulse rounded-3xl"></div>
            </div>
          </template>
        </div>
      </div>

      <!-- Newsletter -->
      <div
          v-if="page.newsletterEnabled"
          class="py-12 flex justify-center transition-opacity duration-700 bg-white"
          :class="pageLoaded ? 'animate-fade-right delay-[1100ms]' : ''"
      >
        <div
            class="w-full max-w-6xl md:mx-0 mx-5 flex flex-col md:flex-row items-center gap-8"
        >
          <!-- Texto + descrição -->
          <div class="flex-1 text-center md:text-left">
            <h2
                class="text-2xl md:text-3xl font-bold"
                :style="{ color: page.colorSecondary || '#00a0f1' }"
            >
              Receba notícias do jogo em primeira mão
            </h2>
            <p class="mt-3 text-gray-700 md:max-w-sm">
              Fique por dentro das novidades, lançamentos e promoções exclusivas.
            </p>
          </div>

          <!-- Formulário -->
          <form
              @submit.prevent="subscribeNewsletter"
              class="flex flex-col sm:flex-row gap-4 w-full md:max-w-md"
          >
            <input
                v-model="newsletterEmail"
                type="email"
                required
                placeholder="Seu e-mail"
                class="flex-1 rounded-lg border-2 px-4 py-3 text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-white"
            />
            <button
                type="submit"
                class="rounded-lg px-6 py-3 font-semibold text-white hover:opacity-90 transition"
                :style="{ backgroundColor: page.colorSecondary || '#00a0f1' }"
            >
              Inscrever-se
            </button>
          </form>
        </div>
      </div>

      <!-- Sponsors -->
      <div :style="{ backgroundColor: page.color || '#111' }"
           class="py-10 transition-opacity duration-700"
           :class="pageLoaded ? 'animate-fade-right delay-1000' : ''">
        <div class="mx-auto max-w-[1600px] px-4">
          <div class="flex flex-wrap justify-center gap-6">
            <template v-if="pageLoaded">
              <div v-for="(group, groupIndex) in page.sponsorGroups" :key="groupIndex"
                   class="min-w-[280px] max-w-fit p-4 rounded-2xl">
                <h3 class="text-xl font-semibold text-center text-white mb-4">{{ group.title }}</h3>
                <div class="flex flex-wrap justify-center gap-4">
                  <div v-for="(logo, logoIndex) in group.logos" :key="logoIndex"
                       class="flex items-center justify-center">
                    <img :src="logo" alt="Logo" class="w-32 sm:w-40 md:w-44 object-contain rounded-2xl" />
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div v-for="index in 2" :key="index" class="min-w-[280px] max-w-fit p-4 bg-white/5 rounded-2xl">
                <div class="h-6 w-48 bg-gray-700 animate-pulse rounded mb-6 mx-auto"></div>
                <div class="flex flex-wrap justify-center gap-4">
                  <div class="h-24 w-32 bg-gray-700 animate-pulse rounded-2xl"></div>
                  <div class="h-24 w-32 bg-gray-700 animate-pulse rounded-2xl"></div>
                  <div class="h-24 w-32 bg-gray-700 animate-pulse rounded-2xl"></div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <Footer
          :language="currentLang"
          :footer-logo="page.footer?.logo"
          :footer-social-links="page.footer?.socialLinks"
      />
    </template>
  </div>
  <div v-else>
    <div class="h-screen w-full flex items-center justify-center">
      <div class="animate-pulse text-3xl text-gray-400">Carregando...</div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useHead } from '@vueuse/head';
import api from '@/services/api';
import Footer from '@/components/sitegame/Footer.vue';
import NavbarGamificacao from '@/components/sitegame/Navbar.vue';
import NavbarEntreterimento from '@/components/sitegamificacao/Navbar.vue';
import CarrosselDesktop from "@/components/sitegame/CarrosselDesktop.vue";
import CarrosselMobile from "@/components/sitegame/CarrosselMobile.vue";
import RenderBlog from './RenderBlog.vue';

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id);
const currentLang = computed(() => route.path.startsWith('/en') ? 'en' : 'pt');

const page = ref({});
const pageLoaded = ref(false);
const newsletterEmail = ref('');

// Navbar dinâmico
const navbarComponent = computed(() => {
  if (page.value.type === 'project') return NavbarEntreterimento;
  if (page.value.type === 'game') return NavbarGamificacao;
  return null;
});

// Youtube – Lógica multilíngue real, usando page.youtubeLink e page.youtubeLinkEn
const youtubeLinkToShow = computed(() => {
  // Preferencialmente o link do idioma atual, se não houver pega do outro idioma
  if (currentLang.value === 'en') {
    return page.value.youtubeLinkEn || page.value.youtubeLink || '';
  }
  return page.value.youtubeLink || page.value.youtubeLinkEn || '';
});
const youtubeTitle = computed(() => {
  if (currentLang.value === 'en') {
    return page.value.title1En || page.value.title1 || '';
  }
  return page.value.title1 || page.value.title1En || '';
});
const youtubeDescription = computed(() => {
  if (currentLang.value === 'en') {
    return page.value.description1En || page.value.description1 || '';
  }
  return page.value.description1 || page.value.description1En || '';
});
const youtubeEmbedLink = computed(() => {
  if (!youtubeLinkToShow.value) return '';
  const videoId = extractYouTubeVideoId(youtubeLinkToShow.value);
  return videoId ? `https://www.youtube.com/embed/${videoId}` : '';
});

function extractYouTubeVideoId(videoLink) {
  const regex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
  const match = videoLink ? videoLink.match(regex) : null;
  return match ? match[1] : null;
}

// Função utilitária para identificar se é HTML ou texto puro
function isHtmlString(str) {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
}

// Carregar página
const loadPage = async () => {
  try {
    const response = await api.getPage(id.value);
    page.value = response.data;
    if (currentLang.value === 'en' && page.value.en) {
      Object.assign(page.value, {
        ...page.value,
        ...page.value.en
      });
    }
    pageLoaded.value = true;
    setMetaTags();
  } catch (error) {
    console.error('Erro ao carregar a página:', error);
  }
};

/* ========== SEO DINÂMICO ========== */
function setMetaTags() {
  if (!page.value || !page.value.title) return;

  const title = page.value.title || 'MitoGames – Jogos e Projetos';
  const description = page.value.description || 'Conheça os jogos e projetos da MitoGames, novidades, lançamentos e mais!';
  const url = typeof window !== 'undefined' ? window.location.href : '';
  let image = '/logo.png';

  if (Array.isArray(page.value.image) && page.value.image.length > 0) {
    image = page.value.image[0];
  } else if (typeof page.value.image === 'string' && page.value.image) {
    image = page.value.image;
  }

  useHead({
    title,
    meta: [
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: image },
      { property: 'og:url', content: url },
      { property: 'twitter:title', content: title },
      { property: 'twitter:description', content: description },
      { property: 'twitter:image', content: image },
      { property: 'twitter:card', content: 'summary_large_image' },
      { name: 'robots', content: 'index, follow' }
    ],
    link: [
      {rel: 'canonical', href: url}
    ]
  });
}

onMounted(loadPage);
watch(() => route.fullPath, loadPage);
watch(page, setMetaTags);

</script>
